import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { eq } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// RadioGroup – reimagined radio button group with Tailwind styling
// ---------------------------------------------------------------------------

export type RadioGroupSize = 'sm' | 'md' | 'lg';

export interface RadioOption {
  /** Unique key for the option */
  key: string;
  /** Display label */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

interface RadioGroupSignature {
  Args: {
    /** Currently selected option key */
    value?: string;
    /** Called when an option is selected */
    onChange?: (value: string) => void;
    /** Label displayed above the group */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Show required indicator on label */
    isRequired?: boolean;
    /** Disable the entire group */
    isDisabled?: boolean;
    /** Layout direction @default "vertical" */
    orientation?: 'vertical' | 'horizontal';
    /** Options to render */
    options: RadioOption[];
    /** Size variant @default "md" */
    size?: RadioGroupSize;
  };
  Element: HTMLFieldSetElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<RadioGroupSize, { radio: string; label: string }> = {
  sm: { radio: 'w-3.5 h-3.5', label: 'text-sm' },
  md: { radio: 'w-4 h-4', label: 'text-sm' },
  lg: { radio: 'w-5 h-5', label: 'text-base' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined RadioGroup — a zero-dependency radio button group component.
 *
 * Renders a group of radio buttons from an `@options` array.
 * Uses violet accent for the selected state, matching other reimagined components.
 *
 * @example Basic usage
 * ```gts
 * import RadioGroup from 'tio-reimagined/components/radio-group';
 *
 * <RadioGroup
 *   @label="Employment Type"
 *   @options={{this.employmentOptions}}
 *   @value={{this.selectedType}}
 *   @onChange={{this.setType}}
 * />
 * ```
 *
 * @example Horizontal with errors
 * ```gts
 * <RadioGroup
 *   @label="Priority"
 *   @orientation="horizontal"
 *   @options={{array
 *     (hash key="low" label="Low")
 *     (hash key="medium" label="Medium")
 *     (hash key="high" label="High")
 *   }}
 *   @value={{this.priority}}
 *   @onChange={{this.setPriority}}
 *   @errors={{array "Priority is required"}}
 *   @isRequired={{true}}
 * />
 * ```
 */
export default class RadioGroup extends Component<RadioGroupSignature> {
  private _groupName = `reimagined-radio-${guidFor(this)}`;

  private get size(): RadioGroupSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  private get isHorizontal(): boolean {
    return this.args.orientation === 'horizontal';
  }

  handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.args.onChange?.(input.value);
  };

  <template>
    <fieldset
      data-test-radio-group
      class="flex flex-col gap-2 {{if @isDisabled 'opacity-50'}}"
      disabled={{@isDisabled}}
      ...attributes
    >
      {{#if @label}}
        <legend data-test-radio-group-label class="text-sm font-medium text-gray-700 mb-1">
          {{@label}}
          {{#if @isRequired}}
            <span class="text-red-500 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
          {{/if}}
        </legend>
      {{/if}}

      {{#if @hint}}
        <p data-test-radio-group-hint class="text-xs text-gray-500 -mt-1 mb-1">{{@hint}}</p>
      {{/if}}

      <div
        class="flex {{if this.isHorizontal 'flex-row flex-wrap gap-x-6 gap-y-2' 'flex-col gap-2'}}"
      >
        {{#each @options as |option|}}
          <label
            data-test-radio-option
            class="flex items-start gap-2.5 cursor-pointer select-none
              {{if option.disabled 'opacity-50 cursor-not-allowed'}}"
          >
            <span class="relative flex-shrink-0 mt-0.5">
              <input
                type="radio"
                name={{this._groupName}}
                value={{option.key}}
                checked={{eq @value option.key}}
                disabled={{option.disabled}}
                class="peer sr-only"
                {{on "change" this.handleChange}}
              />
              <span
                class="block rounded-full border transition-colors duration-150
                  {{this.sizeClasses.radio}}
                  {{if
                    (eq @value option.key)
                    'border-violet-500 dark:border-violet-400'
                    (if
                      this.hasErrors
                      'border-red-500 dark:border-red-400'
                      'border-gray-300 peer-hover:border-gray-400 dark:peer-hover:border-gray-500'
                    )
                  }}
                  peer-focus-visible:ring-2 peer-focus-visible:ring-violet-500/20 peer-focus-visible:ring-offset-1"
              >
                {{#if (eq @value option.key)}}
                  <span class="absolute inset-0 flex items-center justify-center">
                    <span
                      class="block w-1/2 h-1/2 rounded-full bg-violet-500 dark:bg-violet-400"
                    ></span>
                  </span>
                {{/if}}
              </span>
            </span>

            <div class="flex flex-col">
              <span class="{{this.sizeClasses.label}} font-medium text-gray-700">
                {{option.label}}
              </span>
              {{#if option.description}}
                <span class="text-xs text-gray-500 mt-0.5">{{option.description}}</span>
              {{/if}}
            </div>
          </label>
        {{/each}}
      </div>

      {{#if this.hasErrors}}
        <div data-test-radio-group-errors class="space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs text-red-600 dark:text-red-400" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </fieldset>
  </template>
}

export type { RadioGroupSignature };
